import DOMPurify from 'dompurify';
import { getAllWidgets } from '../widget-registry';

const WIDGET_TAG_PATTERN = /^app-[a-z0-9-]+-widget$/;
// Only the options a widget actually reads survive on its element, so an unlisted attribute such as
// formaction or data cannot carry a URL past DOMPurify, which skips its URL check on custom attributes.
const WIDGET_ATTRS = new Set(getAllWidgets().flatMap(widget =>
  widget.editorPropertySchema.propertySchema.map(property => property.key.toLowerCase())));
// In-document fragment references such as url(#gradient) make no request, so they stay like data: images.
// The closing ")" is optional: at the end of input the browser closes an open url( or image-set( itself
// and still loads it.
// Only CSS whitespace may precede data: or #: the URL parser trims ASCII only, so a no-break space would
// turn "data:" into a relative path. Allowing space inside the quote only after a quote keeps it linear.
const EXTERNAL_CSS_URL_PATTERN = /url\((?![ \t\n]*(?:['"][\x01-\x20]*)?(?:data:|#))[^)]*\)?/gi;
const CSS_IMPORT_PATTERN = /@import[^;]*;?/gi;
const CSS_IMAGE_SET_PATTERN = /(-webkit-)?image-set\([^)]*\)?/gi;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const REPLACEMENT_CHAR = '\uFFFD';
// A backslash means an escape could be hiding "url(" or "image-set(".
const SVG_CSS_URL_HINT = /url\(|image-set|\\/i;
const CSS_ESCAPE_PATTERN = /\\([0-9a-f]{1,6})[ \t\n\r\f]?|\\([^\n\r\f0-9a-f])/gi;

// A dedicated instance, so the style hooks below never reach the shared DOMPurify used elsewhere.
const reportPurify = DOMPurify(window);
// nodeName is upper case only for HTML elements; an SVG <style> reports "style" and applies page-wide.
reportPurify.addHook('afterSanitizeElements', node => {
  if (node.nodeName.toUpperCase() === 'STYLE') node.textContent = stripExternalCssUrls(node.textContent ?? '');
});
// SVG presentation attributes (fill, mask, clip-path, filter, marker-*) are parsed as CSS, so a url() in
// them loads a resource just like one in a style attribute.
reportPurify.addHook('afterSanitizeAttributes', element => {
  const isSvg = element.namespaceURI === SVG_NAMESPACE;
  for (const attr of Array.from(element.attributes)) {
    if (attr.name === 'style' || (isSvg && SVG_CSS_URL_HINT.test(attr.value))) {
      element.setAttribute(attr.name, stripExternalCssUrls(attr.value));
    }
  }
  if (element.hasAttribute('target')) element.setAttribute('rel', 'noopener noreferrer');
});

/** Template HTML is authored by users and shared across the organization, so it is cleaned before
 * it reaches the DOM. Widget custom elements and their option attributes are kept so they can still
 * be swapped for their components; FORCE_BODY keeps a template's leading <style> block. Forms are
 * removed with their inputs and buttons, so a template cannot render a credential prompt. */
export function sanitizeReportHtml(html: string): string {
  if (!html) return '';
  return reportPurify.sanitize(html, {
    FORCE_BODY: true,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['form'],
    // ADD_ keeps DOMPurify's default list (script, iframe, ...); FORBID_CONTENTS would replace it.
    ADD_FORBID_CONTENTS: ['form'],
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: WIDGET_TAG_PATTERN,
      attributeNameCheck: (name: string) => WIDGET_ATTRS.has(name.toLowerCase()),
      allowCustomizedBuiltInElements: false
    }
  });
}

/** A div owned by a document with no browsing context: markup parsed into it never loads images or
 * fires handlers such as <img onerror>, unlike a detached div created from the live document. */
export function createInertContainer(): HTMLDivElement {
  return document.implementation.createHTMLDocument('').createElement('div');
}

/** The browser decodes CSS escapes before reading a token, so `u\72l(` is still `url(`. An escaped quote
 * or space is part of the URL text, not a delimiter, so in the copy used for matching it becomes U+FFFD
 * and cannot make "url(\22 data:..." pass as a quoted data: URL. Both copies have the same length. */
function unescapeCss(css: string, forMatching: boolean): string {
  return css.replace(CSS_ESCAPE_PATTERN, (_match, hex?: string, literal?: string) => {
    const char = hex ? decodeHexEscape(parseInt(hex, 16)) : (literal ?? '');
    return forMatching && isExemptionChar(char) ? REPLACEMENT_CHAR : char;
  });
}

/** Code point 0, surrogates and values past U+10FFFF decode to U+FFFD, as the browser does. */
function decodeHexEscape(codePoint: number): string {
  const invalid = codePoint === 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff);
  return invalid ? REPLACEMENT_CHAR : String.fromCodePoint(codePoint);
}

function isExemptionChar(char: string): boolean {
  return char === '"' || char === "'" || (char.length === 1 && char.charCodeAt(0) <= 0x20);
}

/** A removed @import leaves a space, not nothing: none of the patterns can match across a space, so
 * the text around it cannot join into a new rule such as `@im@import x;port "https://..."`, and one
 * pass is enough. The external urls are found in the matching copy and cut from the same positions
 * of the faithful one; the @import and image-set patterns never look at quotes or spaces, so they
 * change both copies alike. */
function removeExternalCss(css: string, matchingCopy: string): { text: string; changed: boolean } {
  const removeRules = (text: string) => text.replace(CSS_IMPORT_PATTERN, ' ').replace(CSS_IMAGE_SET_PATTERN, 'none');
  const output = removeRules(css);
  const urls = Array.from(removeRules(matchingCopy).matchAll(EXTERNAL_CSS_URL_PATTERN));
  const parts: string[] = [];
  let cursor = 0;
  for (const match of urls) {
    parts.push(output.slice(cursor, match.index), 'url("")');
    cursor = match.index + match[0].length;
  }
  parts.push(output.slice(cursor));
  return { text: parts.join(''), changed: urls.length > 0 || output !== css };
}

/** Template styles are copied into the page; an external url(), @import or image-set() would make
 * every viewer's browser call a server chosen by the template author. Inline data: images and
 * url(#fragment) references stay. Matching runs on the escape-decoded copy so escapes cannot hide a
 * url; styles with nothing to remove are returned with only their newlines normalised, which keeps
 * legitimate escapes such as `content: "\201C"`. */
export function stripExternalCssUrls(css: string): string {
  // CSS reads CRLF, CR and FF as one newline before tokenizing, so an escape ended by CRLF is still one
  // escape; normalising first keeps the decoder from leaving a stray newline inside "url(".
  const normalized = css.replace(/\r\n?|\f/g, '\n');
  // A url whose escapes decode to exactly url("") is replaced by the same text, so "unchanged" has to come
  // from what matched; returning the escaped original would let the browser read it as a longer url.
  const { text, changed } = removeExternalCss(unescapeCss(normalized, false), unescapeCss(normalized, true));
  return changed ? escapeDecodedCss(text) : normalized;
}

/** The decoded copy goes back into a style the browser decodes again. A "\" in it can only have come
 * from an escaped backslash, so it is re-escaped first, or a double-escaped `\5c 75rl(` would decode
 * back to `url(`. A literal "<" would let "</style>" close the element when the HTML is parsed again;
 * "<" has no legitimate unescaped use in CSS. */
function escapeDecodedCss(css: string): string {
  return css.replace(/\\/g, '\\5c ').replace(/</g, '\\3c ');
}

/** Collected stylesheet text is placed inside a raw <style> element, so only a closing style tag can
 * break out of it. Other "<" are left alone so media range queries such as `(width < 600px)` keep
 * parsing. */
export function escapeStyleClose(css: string): string {
  return css.replace(/<\/(style)/gi, '\\3c /$1');
}
